import { useLayoutEffect } from 'react'

import useCurrentLocationContext from '@contexts/currentLocationContext'
import useNearbyFeedContext from '@contexts/nearbyFeedContext'
import useVirtuosoSnapshot from '@hooks/useVirtuosoSnapshot'
import useScroll from '@hooks/useScroll'
import styled from '@emotion/styled'

import LocationPermissionDenied from '@components/layout/screen_loaders/LocationPermissionDenied'
import { FeedEmptyPlaceholder } from './feed_elements/FeedEmptyPlaceholder'
import { FeedTopBarClearfix } from './feed_elements/FeedTopBarClearfix'
import CheckNewPostsButton from './post/CheckNewPostsButton'
import PullToRefresh from 'react-simple-pull-to-refresh'
import { FeedFooter } from './feed_elements/FeedFooter'
import { Virtuoso } from 'react-virtuoso'
import Post from './post/Post'


const FeedContainer = styled.div`
   position: relative;
   height: 100vh;
   width: 100vw;
`

const NearbyFeed = ({ setScrollTarget, currentTab }) => {
  const { currentLocationExists, isLocationPermissionDenied } = useCurrentLocationContext()
  const { setScrollRef, scrollRef } = useScroll(setScrollTarget, 500)

  //// State From Context
  const { feedRef, feedState, checkPostsButton } = useNearbyFeedContext()

  // feed state
  const {
    posts,
    postsLoaded,
    setPostsLoaded,
    noMorePosts,
    postsEmpty,
    postHeights,

    getInitialPosts,
    handleInitialPosts,
    getMorePosts,

    setLikeToState,
    setUnlikeToState,
    setRepostToState,
    newReplyAfterCreate,
    afterPostDelete,

    firstRender,
    feedAccessor,
  } = feedState

  // check button state
  const {
    setCheckPostsTimeout,
    getDataOnCheck,
  } = checkPostsButton

  //// Virtuoso Snapshot State
  const {
    restoreStateProp,
    snapshot: feedSnapshot,
  } = useVirtuosoSnapshot(
    'nearby-feed-snapshot',
    firstRender,
    feedRef,
    scrollRef,
  )

  // get posts on initial render
  useLayoutEffect(() => {
    if (!currentLocationExists || isLocationPermissionDenied) return;
    const initialRenderNoSnapshot = (
      currentTab === 2 &&
      firstRender.current === true &&
      Object.keys(feedSnapshot || {})?.length === 0
    )
    const noPostsToDisplay = (
      currentTab === 2 &&
      posts.length === 0
    )
    // NOTE: Get posts on initial render, when there's no snapshot
    //       OR get posts when we have zero posts to display for whatever reason
    const shouldGetPosts = initialRenderNoSnapshot || noPostsToDisplay

    if (shouldGetPosts) {
      getInitialPosts()
    } else {
      firstRender.current = false
      setPostsLoaded(true)
    }
  }, [currentTab, currentLocationExists, isLocationPermissionDenied])

  const getPostsOnCheck = () =>
    getDataOnCheck({ oldData: posts })
      .then((newPosts) => {
        if (newPosts) {
          // if newer posts found, then set new posts and scroll feed to top.
          handleInitialPosts(newPosts)
          feedRef.current.scrollTo({ top: 0 })
        }
      })

  const handleRefresh = () => getInitialPosts()
    .finally(() => setCheckPostsTimeout())

  if (isLocationPermissionDenied) return (
    <FeedContainer>
      <LocationPermissionDenied />
    </FeedContainer>
  )

  return (
    <FeedContainer>      
      <CheckNewPostsButton
        checkButtonState={checkPostsButton}
        onClick={getPostsOnCheck}
      />
      <PullToRefresh
        onRefresh={handleRefresh}
        pullingContent={''}
        isPullable={postsLoaded}>
        <Virtuoso
          scrollerRef={node => { if (node) setScrollRef(node) }}
          ref={node => { if (node) feedRef.current = node  }}
          data={posts}
          context={{
            postsLoaded,
            currentTab,
            postsEmpty,
            posts,
          }}
          heightEstimates={postHeights}
          totalCount={posts?.length}
          // overscan={window.screen.height*3}
          minOverscanItemCount={7}
          endReached={!noMorePosts && currentTab === 2 && getMorePosts}
          atBottomThreshold={window.innerHeight*3}
          itemContent={(index, post) => (
            <Post
              beforeLikeHook={setLikeToState}
              beforeUnlikeHook={setUnlikeToState}
              beforeRepostHook={setRepostToState}
              newReplyAfterCreate={newReplyAfterCreate}
              post={post}
              afterDelete={afterPostDelete}
              feedAccessor={feedAccessor}
              index={index}
              key={post?.uuid || index}
            />
          )}
          { ...restoreStateProp }
          components={{
            Header: FeedTopBarClearfix,
            Footer: FeedFooter,
            EmptyPlaceholder: FeedEmptyPlaceholder
          }}
        />
      </PullToRefresh>
    </FeedContainer>
  )
}

export default NearbyFeed
